import { Components, Theme } from '@mui/material';
import colors from './colors';

/** Primary CTA — brand.primary (Design Tokens 1.0) */
const PRIMARY_BUTTON_BG = colors.primary.main;

/** 카드·패널·테이블·다이얼로그 */
const R = colors.radiusPx.card;
/** 버튼·인풋·필터·셀렉트 */
const RC = colors.radiusPx.control;

/**
 * 테이블 본문·헤더 공통 타이포·패딩 — 모든 페이지가 이 값만 쓴다.
 * size="small"|"medium"과 무관하게 동일. 페이지에서 padding/fontSize를
 * 다시 주면 화면마다 행 높이가 갈라지므로 금지.
 */
const TABLE_FONT = '0.875rem';
const TABLE_FONT_MOBILE = '0.8125rem';
const TABLE_PAD = '16px';
const TABLE_PAD_MOBILE = '12px';

const isIOS =
  typeof navigator !== 'undefined' &&
  /iPhone|iPad|iPod/.test(navigator.userAgent);

/**
 * 주담 브랜드 컴포넌트 스타일 오버라이드
 * radius: control 10px / card 16px (pill·원형 제외)
 * shadow: none(컨트롤) / low(면) / high(오버레이)
 */
const componentStyleOverrides = (theme: Theme): Components<Theme> => ({
  MuiCssBaseline: {
    styleOverrides: {
      body: {
        fontFamily: theme.typography.fontFamily,
        backgroundColor: colors.background.default,
        color: colors.text.primary,
        fontVariantNumeric: 'tabular-nums',
      },
      '@media (prefers-reduced-motion: reduce)': {
        '*, *::before, *::after': {
          animationDuration: '0.01ms !important',
          animationIterationCount: '1 !important',
          transitionDuration: '0.01ms !important',
          scrollBehavior: 'auto !important',
        },
      },
    },
  },
  MuiTextField: {
    defaultProps: {
      ...(isIOS && { InputLabelProps: { shrink: true } }),
    },
  },
  MuiButton: {
    styleOverrides: {
      root: {
        fontWeight: 600,
        borderRadius: RC,
        boxShadow: 'none',
        transition: `transform ${colors.motion.fast} ${colors.motion.easing}, background-color ${colors.motion.standard} ${colors.motion.easing}, border-color ${colors.motion.standard} ${colors.motion.easing}, color ${colors.motion.standard} ${colors.motion.easing}`,
        '&:active': {
          transform: 'scale(0.97)',
        },
        '&.MuiButton-containedPrimary': {
          background: PRIMARY_BUTTON_BG,
          backgroundColor: PRIMARY_BUTTON_BG,
          backgroundImage: 'none',
          color: colors.text.inverse,
          boxShadow: 'none',
          whiteSpace: 'nowrap',
          '&:hover': {
            background: colors.primary.dark,
            backgroundColor: colors.primary.dark,
            backgroundImage: 'none',
            boxShadow: 'none',
          },
          '&.Mui-disabled': {
            backgroundColor: colors.grey[200],
            color: colors.grey[400],
            background: colors.grey[200],
            backgroundImage: 'none',
          },
        },
        '&.MuiButton-containedSecondary': {
          backgroundColor: colors.copper.main,
          color: colors.text.inverse,
          boxShadow: 'none',
          '&:hover': {
            backgroundColor: colors.copper.dark,
            boxShadow: 'none',
          },
          '&.Mui-disabled': {
            backgroundColor: colors.grey[200],
            color: colors.grey[400],
          },
        },
        '&.MuiButton-outlinedPrimary': {
          borderColor: colors.primary.main,
          color: colors.primary.main,
          whiteSpace: 'nowrap',
          '&:hover': {
            backgroundColor: colors.brand.primarySoft,
            borderColor: colors.primary.main,
            color: colors.primary.dark,
          },
          '&.Mui-disabled': {
            borderColor: colors.grey[300],
            color: colors.grey[400],
          },
        },
        '&.Mui-disabled': {
          backgroundColor: colors.grey[200],
          color: colors.grey[400],
          borderColor: colors.grey[300],
        },
      },
    },
  },
  MuiIconButton: {
    styleOverrides: {
      root: {
        boxShadow: 'none',
      },
    },
  },
  MuiPaper: {
    defaultProps: {
      elevation: 0,
    },
    styleOverrides: {
      root: {
        backgroundColor: colors.background.paper,
        backgroundImage: 'none',
        borderRadius: R,
      },
      rounded: {
        borderRadius: R,
      },
      elevation1: { boxShadow: colors.shadow.low },
      elevation2: { boxShadow: colors.shadow.low },
      elevation3: { boxShadow: colors.shadow.low },
      elevation4: { boxShadow: colors.shadow.low },
      elevation6: { boxShadow: colors.shadow.high },
      elevation8: { boxShadow: colors.shadow.high },
      elevation12: { boxShadow: colors.shadow.high },
      elevation16: { boxShadow: colors.shadow.high },
      elevation24: { boxShadow: colors.shadow.high },
    },
  },
  MuiCard: {
    styleOverrides: {
      root: {
        border: `1px solid ${colors.border}`,
        borderRadius: R,
        boxShadow: colors.shadow.low,
        '& .MuiCardHeader-root': {
          padding: '20px',
        },
        '& .MuiCardContent-root': {
          padding: '20px',
        },
      },
    },
  },
  MuiCardHeader: {
    styleOverrides: {
      root: {
        color: colors.text.primary,
        padding: '24px',
      },
    },
  },
  MuiDialogTitle: {
    styleOverrides: {
      root: {
        // 메인 AppBar·모달 헤더와 동일 — brand.chrome
        background: colors.chrome.main,
        backgroundImage: 'none',
        color: colors.text.inverse,
        padding: '16px 24px',
        margin: 0,
        '& .MuiTypography-root': {
          color: colors.text.inverse,
          fontWeight: 600,
        },
        '& .MuiIconButton-root': {
          color: colors.text.inverse,
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
          },
        },
      },
    },
  },
  MuiDialog: {
    styleOverrides: {
      paper: {
        backgroundColor: colors.surface.default,
        borderRadius: R,
        boxShadow: colors.shadow.high,
        transformOrigin: 'center center',
      },
    },
    defaultProps: {
      transitionDuration: { enter: 250, exit: 180 },
    },
  },
  MuiDrawer: {
    styleOverrides: {
      paper: {
        borderRadius: 0,
        boxShadow: colors.shadow.high,
      },
    },
  },
  MuiMenu: {
    defaultProps: {
      transitionDuration: { enter: 180, exit: 120 },
    },
    styleOverrides: {
      paper: {
        transformOrigin: 'var(--mui-menu-transform-origin, top right)',
        borderRadius: R,
        background: colors.surface.default,
        boxShadow: colors.shadow.high,
        border: `1px solid ${colors.border}`,
      },
    },
  },
  MuiPopover: {
    defaultProps: {
      transitionDuration: { enter: 180, exit: 120 },
    },
    styleOverrides: {
      paper: {
        borderRadius: R,
        background: colors.surface.default,
        boxShadow: colors.shadow.high,
        border: `1px solid ${colors.border}`,
      },
    },
  },
  MuiAutocomplete: {
    styleOverrides: {
      paper: {
        borderRadius: R,
        boxShadow: colors.shadow.high,
        border: `1px solid ${colors.border}`,
      },
      listbox: {
        padding: 4,
      },
    },
  },
  MuiDialogActions: {
    styleOverrides: {
      root: {
        padding: '16px 24px',
        '& .MuiButton-containedPrimary': {
          background: PRIMARY_BUTTON_BG,
          backgroundColor: PRIMARY_BUTTON_BG,
          backgroundImage: 'none',
          color: colors.text.inverse,
          boxShadow: 'none',
          '&:hover': {
            background: colors.primary.dark,
            backgroundColor: colors.primary.dark,
            backgroundImage: 'none',
            boxShadow: 'none',
          },
        },
        '& .MuiButton-root[color="primary"]': {
          background: PRIMARY_BUTTON_BG,
          backgroundColor: PRIMARY_BUTTON_BG,
          backgroundImage: 'none',
          color: colors.text.inverse,
          '&:hover': {
            background: colors.primary.dark,
            backgroundColor: colors.primary.dark,
            backgroundImage: 'none',
            boxShadow: 'none',
          },
        },
      },
    },
  },
  MuiAppBar: {
    styleOverrides: {
      root: {
        background: colors.chrome.main,
        backgroundColor: colors.chrome.main,
        backgroundImage: 'none',
        color: colors.text.inverse,
        borderBottom: '1px solid rgba(255, 255, 255, 0.12)',
        boxShadow: 'none',
        borderRadius: 0,
        '& .MuiToolbar-root': {
          background: 'transparent',
          backgroundColor: 'transparent',
          backgroundImage: 'none',
          color: colors.text.inverse,
        },
        // typography variant가 color를 직접 지정해 어두운 크롬 위에서 묻히는 것을 막는다.
        '& .MuiTypography-root': {
          color: 'inherit',
        },
      },
    },
  },
  MuiToolbar: {
    styleOverrides: {
      root: {
        background: 'transparent',
      },
    },
  },
  MuiTableContainer: {
    styleOverrides: {
      root: {
        borderRadius: R,
      },
    },
  },
  MuiTableCell: {
    styleOverrides: {
      root: {
        borderBottom: `1px solid ${colors.divider}`,
        padding: TABLE_PAD,
        backgroundColor: colors.surface.default,
        fontSize: TABLE_FONT,
        lineHeight: 1.45,
        /**
         * 줄바꿈을 막아 행 높이를 항상 같게 유지한다.
         * 폭이 남는 표는 컨테이너가 가로 스크롤되고,
         * tableLayout: fixed로 폭이 고정된 표만 말줄임으로 떨어진다.
         */
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        [theme.breakpoints.down('sm')]: {
          fontSize: TABLE_FONT_MOBILE,
          padding: TABLE_PAD_MOBILE,
        },
      },
      head: {
        backgroundColor: colors.surface.default,
        color: colors.text.primary,
        fontWeight: 600,
        fontSize: TABLE_FONT,
        [theme.breakpoints.down('sm')]: {
          fontSize: TABLE_FONT_MOBILE,
        },
      },
      /** small/medium 동일 — size prop으로 행 높이가 갈라지지 않게 */
      sizeSmall: {
        padding: TABLE_PAD,
        [theme.breakpoints.down('sm')]: {
          padding: TABLE_PAD_MOBILE,
        },
      },
    },
  },
  MuiTableRow: {
    styleOverrides: {
      root: {
        backgroundColor: colors.surface.default,
        '&:hover': {
          backgroundColor: colors.surface.subtle,
        },
      },
    },
  },
  MuiOutlinedInput: {
    styleOverrides: {
      root: {
        borderRadius: RC,
        backgroundColor: colors.surface.default,
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderColor: colors.borderFocus,
          borderWidth: 2,
        },
      },
      input: {
        borderRadius: RC,
        lineHeight: '1.4375em',
      },
      notchedOutline: {
        borderRadius: RC,
      },
    },
  },
  MuiFilledInput: {
    styleOverrides: {
      root: {
        borderRadius: RC,
        '&:before, &:after': { display: 'none' },
      },
    },
  },
  MuiInputBase: {
    styleOverrides: {
      root: {
        borderRadius: RC,
      },
    },
  },
  MuiSelect: {
    styleOverrides: {
      select: {
        borderRadius: RC,
        backgroundColor: colors.surface.default,
      },
      outlined: {
        borderRadius: RC,
      },
    },
  },
  MuiToggleButton: {
    styleOverrides: {
      root: {
        borderRadius: `${RC} !important`,
        boxShadow: 'none',
      },
    },
  },
  MuiToggleButtonGroup: {
    styleOverrides: {
      grouped: {
        borderRadius: `${RC} !important`,
      },
    },
  },
  MuiChip: {
    styleOverrides: {
      root: {
        // 상태/카테고리 칩은 pill 유지 — label.styles에서 제어
        boxShadow: 'none',
      },
      label: {
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      },
    },
  },
  MuiAlert: {
    styleOverrides: {
      root: {
        borderRadius: R,
        boxShadow: 'none',
      },
      standardSuccess: {
        backgroundColor: colors.semantic.successSoft,
        '& .MuiAlert-message': {
          color: colors.semantic.success,
        },
        '& .MuiAlert-icon': {
          color: colors.semantic.success,
        },
      },
      standardError: {
        backgroundColor: colors.semantic.errorSoft,
        '& .MuiAlert-message': {
          color: colors.semantic.error,
        },
        '& .MuiAlert-icon': {
          color: colors.semantic.error,
        },
      },
      standardWarning: {
        backgroundColor: colors.semantic.warningSoft,
        '& .MuiAlert-message': {
          color: colors.semantic.warning,
        },
        '& .MuiAlert-icon': {
          color: colors.semantic.warning,
        },
      },
      standardInfo: {
        backgroundColor: colors.semantic.infoSoft,
        '& .MuiAlert-message': {
          color: colors.semantic.info,
        },
        '& .MuiAlert-icon': {
          color: colors.semantic.info,
        },
      },
    },
  },
  MuiTooltip: {
    styleOverrides: {
      tooltip: {
        backgroundColor: colors.ink.main,
        borderRadius: R,
        boxShadow: colors.shadow.low,
      },
    },
  },
  MuiAccordion: {
    styleOverrides: {
      root: {
        borderRadius: `${R} !important`,
        boxShadow: colors.shadow.low,
        border: `1px solid ${colors.border}`,
        '&:before': { display: 'none' },
        '&.Mui-expanded': {
          margin: 0,
        },
      },
    },
  },
  MuiListItemButton: {
    styleOverrides: {
      root: {
        borderRadius: R,
      },
    },
  },
  MuiFab: {
    styleOverrides: {
      root: {
        boxShadow: colors.shadow.low,
        '&:hover': { boxShadow: colors.shadow.low },
        '&:active': { boxShadow: colors.shadow.low },
      },
    },
  },
  MuiBottomNavigationAction: {
    styleOverrides: {
      root: {
        minWidth: 64,
        '&.Mui-selected': {
          color: colors.primary.main,
        },
      },
      label: {
        fontSize: '0.75rem',
        fontWeight: 500,
        whiteSpace: 'nowrap',
        lineHeight: 1.2,
        '&.Mui-selected': {
          fontSize: '0.75rem',
          fontWeight: 600,
        },
      },
    },
  },
  MuiTab: {
    styleOverrides: {
      root: {
        textTransform: 'none',
        fontWeight: 500,
        borderRadius: R,
        whiteSpace: 'nowrap',
        minHeight: 48,
        '&.Mui-selected': {
          color: colors.primary.main,
          fontWeight: 600,
        },
      },
    },
  },
  MuiTabs: {
    styleOverrides: {
      indicator: {
        backgroundColor: colors.primary.main,
      },
    },
  },
  MuiSkeleton: {
    styleOverrides: {
      root: {
        borderRadius: R,
      },
      rectangular: {
        borderRadius: R,
      },
    },
  },
});

export default componentStyleOverrides;
